import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { Switch } from '@headlessui/react';
import { usePaymentMethods } from '../../context/PaymentMethodContext';
import { usePaymentTypes } from '../../context/PaymentTypeContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { useLoading } from '../../context/LoadingContext';
  
const schema = yup.object().shape({
  name: yup.string().required('Payment method name is required'),
  payment_type_id: yup.string().required('Payment type is required'),
  active: yup.boolean().default(true)
});

const PaymentMethodForm = () => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { addPaymentMethod, updatePaymentMethod, getPaymentMethodById } = usePaymentMethods();
  const { getPaymentTypes } = usePaymentTypes();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/payment-methods');
  const ranOnceRef = useRef(false);
  const ranOnceRef2 = useRef(false);
  const { showLoading, hideLoading } = useLoading();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    watch
  } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if(!id){
      reset({
        name:'',
        payment_type_id:'',
        active:true
      }); 
    }
  }, [id,reset]);

  useEffect(() => {
    const loadData = async () => {
     
      const response = await getPaymentTypes(1, { all: true ,active:true});
      setPaymentTypes(response?.data);
    }
    if(ranOnceRef && !ranOnceRef.current){
      loadData();
      ranOnceRef.current = true
    }
  }, [getPaymentTypes]);

  // Load payment method data
  useEffect(() => {
    const loadData = async () => {
      try {
        showLoading()
        // Load all payment types for dropdown
         
        if (id) {
         
          const methodResponse = await getPaymentMethodById(id);
          setIsLoading(true)
        
          if (methodResponse.status === 200) {
            let  method = methodResponse.data;
            
            const formData = {
              name: method.name,
              payment_type_id: method.payment_type_id || '',
              active: method.active
            };
            setIsActive(method.active);
            reset(formData);
          } else {
            toast.error('Payment method not found');
            navigate(getRoutePath('/payment-methods'));
          }
          hideLoading()
          setIsLoading(false)
        }
      } catch (error) {
        setIsLoading(false)
        hideLoading()
        console.error('Error loading data:', error);
        toast.error('Error loading payment method data');
      } finally {
        setIsLoading(false)
        hideLoading()
        
      }
    };
    if (ranOnceRef2 && !ranOnceRef2.current) {
      loadData();
      ranOnceRef2.current = true;
    }
  }, [id, reset, navigate, getPaymentMethodById, getRoutePath, initialized,getPaymentTypes,hideLoading,showLoading,ranOnceRef2]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading()
    try {
      const formData = {
        ...data,
        active: isActive
      };
      
      let response;
      if (id) {
        // Update existing payment method
        response = await updatePaymentMethod(id, formData);
      } else {
        // Create new payment method
        response = await addPaymentMethod(formData);
      }
      
      if (response.status === 200 || response.status === 201) {
        toast.success(id ? 'Payment method updated successfully!' : 'Payment method created successfully!');
        navigate(getRoutePath('/payment-methods'));
      } else if (response.status === 422) {
        // Handle validation errors
        Object.keys(response.errors).forEach(field => {
          setError(field, { message: response.errors[field][0] });
          toast.error(response.errors[field][0]);
        });
      } else {
        toast.error('Error saving payment method');
      }
      hideLoading()
    } catch (error) {
      toast.error('Error saving payment method');
      console.error('Error:', error);
      hideLoading()
    } finally {
      hideLoading()
      setIsLoading(false);
    }
  };
 
  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {id ? 'Edit Payment Method' : 'Create New Payment Method'}
      </h1>
      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Payment Method Name</label>
            <input
              type="text"
              {...register('name')}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter payment method name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Payment Type</label>
            <select
              {...register('payment_type_id')}
              value={watch('payment_type_id') || ''}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                errors.payment_type_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
            >
              <option value="">Select a payment type</option>
              {paymentTypes?.map((type) => (
                <option key={type.id} value={type.id} >
                  {type.name}
                </option>
              ))}
            </select>
            {errors.payment_type_id && (
              <p className="mt-1 text-sm text-red-600">{errors.payment_type_id.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Switch
                checked={isActive}
                onChange={setIsActive}
                className={`${
                  isActive ? 'bg-[#009efb]' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#009efb] focus:ring-offset-2`}
              >
                <span
                  className={`${
                    isActive ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
              <span className="ml-3 text-sm font-medium text-gray-900">
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(getRoutePath('/payment-methods'))}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  {id ? 'Update Payment Method' : 'Create Payment Method'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentMethodForm; 